class NaptonBookingCalendar extends HTMLElement {
  connectedCallback() {
    this.abort?.abort();
    this.abort = new AbortController();
    this.fleet = JSON.parse(this.querySelector('[data-calendar-fleet]').textContent);
    this.labels = JSON.parse(this.querySelector('[data-calendar-labels]').textContent);
    this.form = this.querySelector('[data-planner]');
    this.fields = this.form.elements;
    this.handoff = this.querySelector('[data-handoff]');
    this.serverDates = new Map();
    const localToday = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
    this.minimum = this.iso(this.add(new Date(`${localToday}T12:00:00`), 1));
    this.fields.arrival.min = this.minimum;
    const initial = this.fleet.find(b => b.handle === this.dataset.boat || b.name === this.dataset.boatName);
    if (!this.dataset.pageBoat && initial) this.dataset.pageBoat = initial.handle;
    this.fields.boat_class.value = initial?.tier || this.dataset.tier || 'elite';
    this.fields.base.value = initial?.base || 'all';
    this.filterBoats(initial?.handle || '');
    const params = new URLSearchParams(location.search);
    for (const name of ['boat_class', 'base', 'duration', 'guests']) {
      const value = params.get(`nb_${name}`);
      if (value && [...this.fields[name].options].some(o => o.value === value)) this.fields[name].value = value;
    }
    this.filterBoats(params.get('nb_boat') ?? initial?.handle ?? '');
    const arrival = params.get('nb_arrival');
    if (this.validISO(arrival) && arrival >= this.minimum) this.fields.arrival.value = arrival;
    const start = this.fields.arrival.value || this.minimum;
    this.month = new Date(`${start.slice(0,7)}-01T12:00:00`);
    this.addEventListener('click', e => {
      if (!(e.target instanceof Element)) return;
      const monthButton = e.target.closest('[data-month-step]');
      if (monthButton) {
        this.month = new Date(this.month.getFullYear(), this.month.getMonth() + Number(monthButton.dataset.monthStep), 1, 12);
        this.drawCalendar(); this.refreshAvailability();
      }
      const day = e.target.closest('[data-date]');
      if (day && !day.disabled) {
        this.fields.arrival.value = day.dataset.date;
        this.update(true);
        this.querySelector(`[data-date="${day.dataset.date}"]`)?.focus({ preventScroll: true });
      }
      if (e.target.closest('[data-copy]')) this.copyPlans();
      if (e.target.closest('[data-enquire]')) this.enquire();
    }, { signal: this.abort.signal });
    this.form.addEventListener('change', e => {
      if (e.target.name === 'boat') {
        const boat = this.fleet.find(b => b.handle === e.target.value);
        if (boat) { this.fields.boat_class.value = boat.tier; this.fields.base.value = boat.base; }
      }
      if (['boat_class','base','guests'].includes(e.target.name)) this.filterBoats(this.fields.boat.value);
      if (e.target.name === 'arrival' && this.validISO(this.fields.arrival.value)) this.month = new Date(`${this.fields.arrival.value.slice(0,7)}-01T12:00:00`);
      this.update(true); this.refreshAvailability();
    }, { signal: this.abort.signal });
    this.form.addEventListener('submit', e => { e.preventDefault(); this.prepareHandoff(); }, { signal: this.abort.signal });
    this.update(false);
    if (['#calendar-request','#holiday-enquiry'].includes(location.hash) && this.fields.arrival.value) { this.prepareHandoff(); this.enquire(); }
  }
  disconnectedCallback() { this.abort?.abort(); this.requestAbort?.abort(); }
  validISO(value) { return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) && this.iso(new Date(`${value}T12:00:00`)) === value; }
  iso(d) { return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; }
  add(d, n) { const result = new Date(d); result.setDate(result.getDate()+n); return result; }
  display(value) { return new Intl.DateTimeFormat(document.documentElement.lang || 'en-GB', {day:'numeric',month:'short',year:'numeric'}).format(new Date(`${value}T12:00:00`)); }
  matches() { return this.fleet.filter(b => b.tier === this.fields.boat_class.value && (this.fields.base.value === 'all' || b.base === this.fields.base.value) && b.capacity >= Number(this.fields.guests.value)); }
  filterBoats(preferred) {
    const choices = this.matches();
    this.fields.boat.replaceChildren(new Option(this.labels.all, ''));
    for (const b of choices) this.fields.boat.add(new Option(`${b.name} · ${this.labels[b.base]} · ${b.capacity}`, b.handle));
    this.fields.boat.value = choices.some(b => b.handle === preferred) ? preferred : '';
  }
  restricted(value) {
    const day = new Date(`${value}T12:00:00`).getDay();
    const n = Number(this.fields.duration.value);
    if (n === 3 || n === 10) return day !== 5;
    if (n === 4 || n === 11) return day !== 1;
    return ![1,5,6].includes(day);
  }
  selection() { return Object.fromEntries(new FormData(this.form)); }
  update(persist) {
    this.handoff.hidden = true;
    this.querySelector('[data-error]').hidden = true;
    this.querySelector('[data-scope]').textContent = this.fleet.find(b => b.handle === this.fields.boat.value)?.name || this.labels[this.fields.boat_class.value];
    this.querySelector('[data-fleet-status]').textContent = this.labels.boats_count.replace('[count]', String(this.matches().length));
    const start = this.fields.arrival.value;
    this.end = this.validISO(start) ? this.iso(this.add(new Date(`${start}T12:00:00`), Number(this.fields.duration.value))) : '';
    this.querySelector('[data-return]').textContent = this.end ? this.display(this.end) : this.labels.choose;
    this.querySelector('[data-planner] button[type=submit]').disabled = !this.matches().length;
    this.drawCalendar();
    if (persist) {
      const url = new URL(location.href);
      for (const [name,value] of Object.entries(this.selection())) url.searchParams.set(`nb_${name}`,value);
      try { history.replaceState(history.state,'',url); } catch {}
    }
  }
  drawCalendar() {
    const grid = this.querySelector('[data-days]');
    grid.replaceChildren();
    this.querySelector('[data-month-title]').textContent = new Intl.DateTimeFormat(document.documentElement.lang || 'en-GB',{month:'long',year:'numeric'}).format(this.month);
    const minMonth = this.minimum.slice(0,7);
    this.querySelector('[data-month-step="-1"]').disabled = this.iso(this.month).slice(0,7) <= minMonth;
    const offset = (this.month.getDay()+6)%7;
    for(let i=0;i<offset;i++) grid.append(document.createElement('span'));
    const count = new Date(this.month.getFullYear(),this.month.getMonth()+1,0).getDate();
    for(let day=1;day<=count;day++) {
      const value = this.iso(new Date(this.month.getFullYear(),this.month.getMonth(),day,12));
      const button = document.createElement('button');
      button.type='button'; button.className='nbc-day'; button.textContent=day; button.dataset.date=value;
      const receipt = this.serverDates.get(value);
      let state = this.checking ? 'loading' : receipt?.state || 'unchecked';
      if (this.restricted(value) || receipt?.state === 'restricted') state='restricted';
      if(value === this.end) state='return';
      else if(value > this.fields.arrival.value && this.end && value < this.end) state='stay';
      else if(value === this.fields.arrival.value) state='selected';
      button.dataset.state=state;
      button.disabled = value < this.minimum || this.restricted(value) || receipt?.state === 'unavailable' || receipt?.state === 'restricted';
      button.setAttribute('aria-pressed',String(value===this.fields.arrival.value));
      const status = this.labels[state] || this.labels.selected;
      button.setAttribute('aria-label',`${this.display(value)} · ${status}`);
      if(receipt?.state==='available') button.dataset.verified='true';
      grid.append(button);
    }
    for(let i=offset+count;i<42;i++) grid.append(document.createElement('span'));
  }
  setAvailabilityProvider(provider) {
    if (typeof provider !== 'function') throw new TypeError('Availability provider must be a server-backed function');
    this.provider = provider;
    this.refreshAvailability();
  }
  async refreshAvailability() {
    this.requestAbort?.abort();
    this.serverDates.clear();
    if (!this.provider) return;
    const abort = new AbortController(); this.requestAbort=abort; this.checking=true; this.drawCalendar();
    try {
      const result = await this.provider({ ...this.selection(), month:this.iso(this.month).slice(0,7) }, { signal:abort.signal });
      if(abort.signal.aborted) return;
      if(!result || !result.source || !result.checkedAt || !Array.isArray(result.dates)) throw new Error('Missing verification receipt');
      for(const date of result.dates) if(this.validISO(date.date) && ['available','unavailable','restricted'].includes(date.state)) this.serverDates.set(date.date,date);
    } catch(error) {
      if(abort.signal.aborted) return;
      this.serverDates.clear(); this.showError(this.labels.error);
    } finally { if(!abort.signal.aborted) { this.checking=false; this.drawCalendar(); } }
  }
  showError(message) { const error=this.querySelector('[data-error]');error.textContent=message;error.hidden=false; }
  prepareHandoff() {
    const start=this.fields.arrival.value;
    if(!this.validISO(start) || start<this.minimum || this.restricted(start)) { this.showError(this.labels.invalid);this.fields.arrival.focus();return; }
    if(!this.matches().length) { this.showError(this.labels.empty);return; }
    const b=this.fleet.find(b=>b.handle===this.fields.boat.value);
    const startDate=new Date(`${start}T12:00:00`);
    const receipt=this.serverDates.get(start);
    if(receipt && ['unavailable','restricted'].includes(receipt.state)) { this.showError(this.labels.unavailable);return; }
    this.summary = `${b?.name || this.labels[this.fields.boat_class.value]} · ${this.fields.base.value === 'all' ? this.labels.either : this.labels[this.fields.base.value]} · ${this.display(start)} → ${this.display(this.end)} · ${this.fields.duration.value} ${this.labels.nights} · ${this.fields.guests.value} ${this.labels.guests.toLowerCase()}`;
    this.querySelector('[data-summary]').textContent=this.summary;
    this.querySelector('[data-booking-handoff]').hidden = false;
    this.handoff.querySelector('.nbc-small').textContent = this.labels.handoff;
    const f=this.querySelector('[data-booking-handoff]').elements;
    f.start_date.value=`${String(startDate.getDate()).padStart(2,'0')}/${String(startDate.getMonth()+1).padStart(2,'0')}/${startDate.getFullYear()}`;
    f.duration.value=String(({4:3,11:10})[Number(this.fields.duration.value)] || this.fields.duration.value);
    f.num_persons.value=this.fields.guests.value;
    f.base_id.value=this.fields.base.value==='all'?'Either Base':this.labels[this.fields.base.value];
    const model=this.querySelector('[data-model]');
    model.hidden=!b; this.querySelector('[data-model-note]').hidden=!b;
    if(b) { const url=new URL(b.booking); if(url.protocol==='https:' && url.hostname==='www.napton-marina.co.uk') model.href=url.href+'#holiday_finder'; }
    this.querySelector('[data-enquire]').hidden=false;
    this.querySelector('[data-enquire]').textContent = b ? this.labels.preferred : this.labels.ask_team;
    this.handoff.hidden=false;this.handoff.focus({preventScroll:true});
    this.handoff.scrollIntoView({behavior:matchMedia('(prefers-reduced-motion: reduce)').matches?'instant':'smooth',block:'nearest'});
  }
  async copyPlans() {
    try { await navigator.clipboard.writeText(this.summary); this.querySelector('[data-copy-status]').textContent=this.labels.copied; }
    catch { this.querySelector('[data-summary]').focus(); }
  }
  enquire() {
    const boat=this.fleet.find(b=>b.handle===this.fields.boat.value);
    const generic = document.querySelector('form[data-holiday-enquiry]');
    if (generic) {
      generic.querySelector('[data-holiday-summary]').value=this.summary;
      generic.querySelector('[name="contact[body]"]').value=this.summary;
      generic.scrollIntoView({behavior:matchMedia('(prefers-reduced-motion: reduce)').matches?'instant':'smooth',block:'start'});
      generic.querySelector('[name="contact[name]"]').focus({preventScroll:true});
      return;
    }
    if(!boat) {
      const url = new URL(`${window.Shopify?.routes?.root || '/'}pages/book-your-holiday`,location.origin);
      for(const [key,value] of Object.entries(this.selection())) url.searchParams.set(`nb_${key}`,value);
      url.hash='holiday-enquiry';
      location.assign(url.href);
      return;
    }
    const current=this.dataset.pageBoat;
    const container=this.closest('napton-fleet-detail,napton-boat-page');
    const contact=container?.querySelector('form[action*="contact"]');
    if(boat.handle===current && contact) {
      const date=contact.querySelector('[name="contact[Preferred departure]"]');if(date) date.value=this.fields.arrival.value;
      const guests=contact.querySelector('[name="contact[Guests]"],[name="contact[Party size]"]');if(guests)guests.value=this.fields.guests.value;
      const length=contact.querySelector('[name="contact[Holiday length]"]');if(length){const value=`${this.fields.duration.value} ${this.labels.nights}`;if(length.tagName==='SELECT')length.add(new Option(value,value));length.value=value;}
      const body=contact.querySelector('[name="contact[body]"]');if(body)body.value=this.summary;
      contact.scrollIntoView({behavior:matchMedia('(prefers-reduced-motion: reduce)').matches?'instant':'smooth',block:'center'});
      contact.querySelector('[name="contact[name]"]')?.focus({preventScroll:true});
    } else if (boat.handle === current) {
      this.showError(this.labels.enquiry_note);
    } else {
      const url=new URL(boat.url || `${window.Shopify?.routes?.root || '/'}products/${boat.handle}`,location.origin);
      for(const [key,value] of Object.entries(this.selection()))url.searchParams.set(`nb_${key}`,value);
      const preview=new URLSearchParams(location.search).get('preview_theme_id');if(preview)url.searchParams.set('preview_theme_id',preview);
      url.hash='calendar-request';location.assign(url.href);
    }
  }
}
if(!customElements.get('napton-booking-calendar'))customElements.define('napton-booking-calendar',NaptonBookingCalendar);


